import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  isValidate,
  isFromValid,
  DescriptionTextArea,
  InputSwitch,
  DropDownInput,
} from 'smart-react';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Error } from '@progress/kendo-react-labels';
import { Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { buildNotification, GenerateNotification } from 'smart-react';
import { listIntegrationTypes } from '../../IntegrationType/Services/IntegrationTypeService';
import {
  createConnectionType,
  updateConnectionType,
  getConnectionType,
} from '../Services/ConnectionTypeService';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { LOOKUP_TYPES } from '../../../../constants/applicationConstants';
import { retrieveAndDecrypt } from '../../../../Utils/Storage/localStorageUtil';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Connection Type main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Connection Type component.
 */
const AddConnectionType = () => {
  const navigate = useNavigate();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const integrationSystem = getParam('integrationSystem');
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isEditor, setIsEditor] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [system, setSystem] = React.useState({});
  const { state } = useLocation();
  const [tempState, setTempState] = React.useState(state);
  const [formData, setFormData] = React.useState({
    SystemConnTypeId: '',
    SysType: system?.SysType,
    ConnectionType: '',
    Version: '',
    Description: '',
    AuthType: '',
    Active: true,
  });
  const authTypes = retrieveAndDecrypt(LOOKUP_TYPES?.authTypes);
  const requiredFields = ['ConnectionType', 'SysType', 'Description'];
  const [selected, setSelected] = React.useState(0);
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [editorField, setEditorField] = React.useState({
    name: '',
    value: '',
    label: '',
  });

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * fetch Connection Type Data From API
   * @param {*} stateData
   */
  const fetchConnectionTypeData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getConnectionType({
        data: refObj,
      });
      const payloadData = response.Payload;
      if (payloadData) {
        setTempState({ ...stateData, dataSet: payloadData, edit: true });
        setFormData(payloadData);
        setNoRecord(false);
        setDetailsDataModified(true);
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState?.edit || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchConnectionTypeData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setDetailsDataModified(true);
        setShouldUpdate(!shouldUpdate);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  React.useEffect(() => {
    let groupRef = base64ToObject(integrationSystem);
    if (groupRef) {
      setSystem(groupRef);
      setFormData((prevState) => ({
        ...prevState,
        SysType: groupRef?.SysType,
      }));
    }
  }, []);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the test data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;
    if (tempState?.edit) {
      method = updateConnectionType;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createConnectionType;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        if (!tempState?.edit) {
          setFormData({
            SystemConnTypeId: '',
            SysType: system?.SysType,
            ConnectionType: '',
            Version: '',
            Description: '',
            AuthType: '',
            Active: true,
          });
        }
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        // Update tempState to trigger rerender
        setTempState({
          ...tempState,
          dataSet: response?.Payload,
        });
        if (isContinue) {
          const payloadData = response?.Payload;
          const refId = objectToBase64({
            SysType: payloadData?.SysType,
            SystemConnTypeId: payloadData?.SystemConnTypeId,
          });
          navigate(
            `/admin/connection-type?refId=${refId}&integrationSystem=${integrationSystem}`,
            {
              state: {
                edit: true,
                dataSet: response?.Payload,
                systemType: system,
              },
            },
          );
        }
        if (isExit) {
          navigate(`/admin/integration-type?refId=${integrationSystem}`, {
            state: {
              edit: true,
              dataSet: system,
            },
          });
        }
      }
    } catch (error) {
      console.log('error', error);
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to integration type details screen
   */
  const onExit = () => {
    navigate(`/admin/integration-type?refId=${integrationSystem}`, {
      state: {
        edit: true,
        dataSet: system,
      },
    });
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={'No Data found'}
              onExit={onExit}
            />
          ) : (
            <>
              <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                <DescriptionTextArea
                  onChange={handleInputChange}
                  label={'Description'}
                  renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                  validateForm={validateForm}
                  formData={formData}
                  setFormData={setFormData}
                  detailsDataModified={detailsDataModified}
                  isTextArea={isTextArea}
                  setIsTextArea={setIsTextArea}
                  editorField={editorField}
                  setEditorField={setEditorField}
                  row={2}
                />
              </div>
              <div
                className={`add-test main-page-section add-page-section ${!isEditor ? '' : 'k-d-none'}`}
              >
                <Card className='tophead'>
                  <CardHeader>
                    <div className='action-buttons-container'>
                      <span
                        className='action-icon-wrapper !k-d-block'
                        onClick={() => onExit()}
                      >
                        <FontAwesomeIcon
                          className='k-mr-2 k-pt-3'
                          icon={faArrowLeft}
                        />
                      </span>
                      <p className='k-align-items-center k-d-flex k-mb-0 display-text'>
                        Connection Type: {formData?.ConnectionType}
                      </p>
                    </div>
                  </CardHeader>
                </Card>
                <Card className='k-mt-3 detail-card'>
                  <CardHeader>
                    <h6 className='card-title-secondary'>Details</h6>
                  </CardHeader>
                  <CardBody className='detail-card-body'>
                    <div>
                      <Form
                        onSubmit={onSave}
                        render={(formRenderProps) => (
                          <div className='card-content'>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData.ConnectionType) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'ConnectionType'}
                                  id={'ConnectionType'}
                                  name={'ConnectionType'}
                                  label={'Connection Type'}
                                  data={formData?.ConnectionType}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData.ConnectionType,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.SysType) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'SysType'}
                                  id={'SysType'}
                                  name={'SysType'}
                                  label={'System Type'}
                                  data={formData?.SysType}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                  requiredField={true}
                                  renderTypes={'ReadOnly'}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.SysType,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <DropDownInput
                                  label='Auth Type'
                                  textField='DisplayName'
                                  dataItemKey='Value'
                                  name={'AuthType'}
                                  dataSet={authTypes}
                                  value={
                                    formData?.AuthType
                                      ? {
                                          Value: formData?.AuthType,
                                          DisplayName: formData?.AuthType,
                                        }
                                      : null
                                  }
                                  handleChange={(e) => {
                                    setFormData((prevState) => ({
                                      ...prevState,
                                      AuthType: e.value?.Value,
                                    }));
                                  }}
                                  defaultItem={{
                                    DisplayName: 'Select Auth Type ...',
                                    Value: '',
                                  }}
                                  filterable={true}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'Version'}
                                  id={'Version'}
                                  name={'Version'}
                                  label={'Version'}
                                  data={formData?.Version}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper field-wrapper-description field-wrapper-100 ${isValidate(validateForm, formData?.Description) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <DescriptionTextArea
                                  onChange={handleInputChange}
                                  label={'Description'}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  validateForm={validateForm}
                                  formData={formData}
                                  setFormData={setFormData}
                                  detailsDataModified={detailsDataModified}
                                  isTextArea={isTextArea}
                                  setIsTextArea={setIsTextArea}
                                  editorField={{
                                    name: 'Description',
                                    value: formData?.Description,
                                    label: 'Description',
                                  }}
                                  setEditorField={setEditorField}
                                  row={2}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.Description,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='single-field-row k-mt-4 single-row-60'>
                              <div className={`field-wrapper`}>
                                <Field
                                  name='Active'
                                  component={InputSwitch}
                                  label='Active'
                                  onLabel='Yes'
                                  offLabel='No'
                                  id='Active'
                                  checked={formData?.Active}
                                  onChange={handleInputChange}
                                  disabled={!detailsDataModified}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>
                {detailsDataModified && (
                  <Card className='k-mt-3'>
                    <CardHeader>
                      <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                        <>
                          <Button
                            icon={'cancel'}
                            onClick={() => onExit()}
                            type='button'
                          >
                            Cancel
                          </Button>
                          <SplitButton
                            text='save'
                            themeColor={'primary'}
                            onButtonClick={() =>
                              onSave({ isExit: false, isContinue: true })
                            }
                            onItemClick={handleSaveSplit}
                          >
                            <SplitButtonItem text='Save & Close' />
                          </SplitButton>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                )}
                {tempState?.edit && (
                  <Card className='k-mt-3 detail-grid-section'>
                    <CardBody className='!k-px-0'>
                      <DesktopLayout
                        handleSelect={handleSelect}
                        selected={selected}
                        detailsDataModified={detailsDataModified}
                        state={tempState}
                        shouldUpdate={shouldUpdate}
                        setShouldUpdate={setShouldUpdate}
                      />
                    </CardBody>
                  </Card>
                )}
              </div>
            </>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddConnectionType;
